import axios from "axios";
import { spire360ApiClient } from "./Spire360ApiClient";

// AI-summary endpoints live on patients-experience-api (keyed by patientId, not appointmentId) -
// see AI_SUMMARY_BACKEND_HANDOFF.md. The patients-experience-client generated package does not
// yet expose methods for these two routes, so this helper hits them directly through the shared,
// already-authenticated spire360ApiClient instance, using the same REACT_APP_PATIENTS_API_URL(_GATEWAY)
// base path convention as the rest of this codebase's patients-experience-api calls (see PatientApiHelper.ts).
//
// Swap-over path once real generated methods exist: rewrite only the two function bodies below to
// delegate to createPatientApi() (or a dedicated generated client). The ClinicianAiSummaryApi
// interface and every call site (useClinicianAiSummary) stay unchanged.

export type ClinicianAiSummaryStatus = "pending" | "processing" | "completed" | "failed";

export const COPD_PRE_VISIT_SUMMARY_TYPE = "copd_pre_visit";

export interface ClinicianAiSummaryDto {
  id: string;
  patientId: string;
  status: ClinicianAiSummaryStatus;
  generatedSummary: string | null;
  modelId: string | null;
  promptVersion: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ClinicianAiSummaryApi {
  getSummary(patientId: string): Promise<{ data: ClinicianAiSummaryDto | null; status: number }>;
  generateSummary(patientId: string): Promise<{ data: ClinicianAiSummaryDto; status: number }>;
}

// Generation is now a single synchronous call through patients-experience-api
// (assemble prompt -> Bedrock -> persisted result, ~25s server-side budget),
// so this needs headroom above that instead of the shared client's default.
// getSummary is just a status read and can stay snappy.
const GET_SUMMARY_TIMEOUT_MS = 10000;
const GENERATE_SUMMARY_TIMEOUT_MS = 30000;

function getBasePath(): string {
  const isGatewayEnabled = process.env.REACT_APP_CONFIG_GATEWAY === "true";
  return isGatewayEnabled
    ? `${process.env.REACT_APP_PATIENTS_API_URL_GATEWAY}`
    : `${process.env.REACT_APP_PATIENTS_API_URL}`;
}

export function createClinicianAiSummaryApi(): ClinicianAiSummaryApi {
  return {
    getSummary: async (patientId: string) => {
      try {
        const response = await spire360ApiClient.get(
          `${getBasePath()}/patients/${patientId}/ai-summary`,
          {
            params: { summaryType: COPD_PRE_VISIT_SUMMARY_TYPE },
            timeout: GET_SUMMARY_TIMEOUT_MS,
          }
        );
        return { data: response.data ?? null, status: response.status };
      } catch (error) {
        // TODO: confirm the actual not-found contract (200+null vs 404) once experience-api ships.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return { data: null, status: 404 };
        }
        throw error;
      }
    },
    generateSummary: async (patientId: string) => {
      const response = await spire360ApiClient.post(
        `${getBasePath()}/patients/${patientId}/ai-summary/generate`,
        { summaryType: COPD_PRE_VISIT_SUMMARY_TYPE },
        { timeout: GENERATE_SUMMARY_TIMEOUT_MS }
      );
      return { data: response.data, status: response.status };
    },
  };
}
